import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Check, Download, ExternalLink, FileImage, Upload, Video } from "lucide-react";

const DesignToolsPage = () => {
  const [showCanvaProject, setShowCanvaProject] = useState(false);
  const [showAdobeProject, setShowAdobeProject] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [projectName, setProjectName] = useState("");

  const canvaTemplates = [
    {
      id: "template1",
      name: "YouTube Thumbnail",
      image: "https://storage.googleapis.com/fenado-ai-farm-public/generated/a5f42e32-8599-414a-8a7e-2e02b0dbf6cb.webp",
      description: "Template para thumbnails do YouTube com espaço para título e imagem"
    },
    {
      id: "template2",
      name: "Instagram Post",
      image: "https://storage.googleapis.com/fenado-ai-farm-public/generated/e4cd4307-8d5a-4c93-8c56-97cea100f233.webp",
      description: "Template para posts do Instagram com layout moderno"
    },
    {
      id: "template3",
      name: "Pinterest Pin",
      image: "https://storage.googleapis.com/fenado-ai-farm-public/generated/cf1577dc-8a9a-4da9-bc0e-e642c4c262fb.webp",
      description: "Template vertical para Pinterest com espaço para imagem e texto"
    },
    {
      id: "template4",
      name: "Blog Banner",
      image: "https://storage.googleapis.com/fenado-ai-farm-public/generated/2c2884f0-0e7a-4e0d-a3a1-9b68dd3cc7ab.webp",
      description: "Banner para cabeçalho de blog com espaço para título"
    }
  ];

  const adobeTemplates = [
    {
      id: "adobe1",
      name: "YouTube Intro",
      image: "https://storage.googleapis.com/fenado-ai-farm-public/generated/e6e80f1f-a7ba-4eba-9d9a-4a6f9fd4c1a4.webp",
      description: "Animação de introdução para vídeos do YouTube"
    },
    {
      id: "adobe2",
      name: "Video Transition",
      image: "https://storage.googleapis.com/fenado-ai-farm-public/generated/ab733b94-c614-4b83-b59a-bcf0134b37a6.webp",
      description: "Transição suave entre cenas de vídeo"
    },
    {
      id: "adobe3",
      name: "Lower Third",
      image: "https://storage.googleapis.com/fenado-ai-farm-public/generated/b8c8d55a-c7e2-4e2a-b268-573c9aa9f1a5.webp",
      description: "Elemento de identificação para parte inferior do vídeo"
    },
    {
      id: "adobe4",
      name: "End Screen",
      image: "https://storage.googleapis.com/fenado-ai-farm-public/generated/18d8fbe9-8b72-4e6d-8c52-e7fa52b02950.webp",
      description: "Tela final para vídeos com call-to-action"
    }
  ];

  const canvaTutorials = [
    {
      title: "Como criar um banner para YouTube",
      steps: [
        "Acesse o Canva e clique em 'Criar um design'",
        "Selecione 'Capa para YouTube'",
        "Escolha um template ou comece do zero",
        "Adicione texto, imagens e elementos gráficos",
        "Ajuste as cores e fontes conforme seu estilo",
        "Baixe a imagem em alta resolução"
      ]
    },
    {
      title: "Como criar um post para Instagram",
      steps: [
        "No Canva, selecione 'Post para Instagram'",
        "Escolha um template que combine com seu conteúdo",
        "Substitua as imagens por suas próprias fotos",
        "Edite o texto para sua mensagem",
        "Adicione sua marca d'água ou logo",
        "Exporte e publique no Instagram"
      ]
    }
  ];

  const adobeTutorials = [
    {
      title: "Como criar uma animação para YouTube",
      steps: [
        "Abra o Adobe Express e selecione 'Vídeo'",
        "Escolha um template de animação",
        "Substitua o texto e imagens pelos seus",
        "Ajuste a duração das cenas",
        "Adicione música de fundo da biblioteca",
        "Exporte em alta qualidade para o YouTube"
      ]
    },
    {
      title: "Como criar uma transição de vídeo",
      steps: [
        "No Adobe Express, abra seu projeto de vídeo",
        "Selecione o ponto entre duas cenas",
        "Escolha um efeito de transição da biblioteca",
        "Ajuste a duração da transição",
        "Visualize o resultado",
        "Salve o projeto e exporte o vídeo final"
      ]
    }
  ];

  const handleImageSelect = (image: string) => {
    setSelectedImage(image);
  };

  const handleCreateProject = () => {
    if (!projectName) {
      toast.error("Por favor, dê um nome ao seu projeto.");
      return;
    }
    
    toast.success(`Projeto "${projectName}" criado com sucesso!`);
    setProjectName("");
    setSelectedImage(null);
    setShowCanvaProject(false);
    setShowAdobeProject(false);
  };

  const handleDownloadCanva = () => {
    toast.success("Download do Canva iniciado!");
    window.open("https://www.canva.com/download/windows/", "_blank");
  };

  const handleOpenAdobeExpress = () => {
    window.open("https://www.adobe.com/express/create/animation/youtube", "_blank");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Ferramentas de Design e Criação
          </h1>
          <p className="text-muted-foreground max-w-3xl mx-auto mb-6">
            Crie designs profissionais, vídeos e animações para suas redes sociais, blogs e canais usando o Canva e Adobe Express.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <Card className="overflow-hidden">
              <img 
                src="https://storage.googleapis.com/fenado-ai-farm-public/generated/6e6751c8-8d0a-4d08-bc06-4c6a7a77b1e9.webp"
                alt="Canva"
                className="w-full h-48 object-cover"
              />
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-2">Canva</h2>
                <p className="text-muted-foreground mb-4">
                  Ferramenta de design gráfico online para criar posts de mídia social, apresentações, banners e muito mais.
                </p>
                <Button 
                  className="w-full"
                  onClick={handleDownloadCanva}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Baixar Canva para Windows
                </Button>
              </CardContent>
            </Card>
            
            <Card className="overflow-hidden">
              <img 
                src="https://storage.googleapis.com/fenado-ai-farm-public/generated/1de9661d-5f04-4c60-8a04-d30f9df9bff7.webp"
                alt="Adobe Express"
                className="w-full h-48 object-cover"
              />
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-2">Adobe Express</h2>
                <p className="text-muted-foreground mb-4">
                  Crie animações para YouTube, vídeos profissionais e conteúdo visual de alta qualidade.
                </p>
                <Button 
                  className="w-full"
                  onClick={handleOpenAdobeExpress}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Abrir Adobe Express
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        <Tabs defaultValue="canva" className="max-w-5xl mx-auto">
          <TabsList className="grid grid-cols-2 mb-8">
            <TabsTrigger value="canva">Canva</TabsTrigger>
            <TabsTrigger value="adobe">Adobe Express</TabsTrigger>
          </TabsList>

          <TabsContent value="canva">
            <Card>
              <CardHeader>
                <CardTitle>Canva - Criação de Designs</CardTitle>
                <CardDescription>
                  Templates e tutoriais para criar designs incríveis para suas redes sociais e blogs
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold">Templates populares</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                    {canvaTemplates.map((template) => (
                      <Card 
                        key={template.id} 
                        className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => setShowCanvaProject(true)}
                      >
                        <div className="aspect-video relative">
                          <img 
                            src={template.image} 
                            alt={template.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <CardContent className="p-3">
                          <h4 className="font-medium text-sm">{template.name}</h4>
                          <p className="text-xs text-muted-foreground mt-1">{template.description}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold">Tutoriais</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {canvaTutorials.map((tutorial, index) => (
                      <Card key={index} className="overflow-hidden">
                        <CardHeader>
                          <CardTitle className="text-lg">{tutorial.title}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ol className="space-y-2">
                            {tutorial.steps.map((step, stepIndex) => (
                              <li key={stepIndex} className="flex items-start">
                                <span className="bg-primary/20 text-primary rounded-full h-5 w-5 flex items-center justify-center mr-2 text-xs flex-shrink-0 mt-0.5">
                                  {stepIndex + 1}
                                </span>
                                <span className="text-sm">{step}</span>
                              </li>
                            ))}
                          </ol>
                        </CardContent>
                        <CardFooter>
                          <Button 
                            variant="outline" 
                            className="w-full"
                            onClick={() => window.open("https://www.canva.com", "_blank")}
                          >
                            Experimentar no Canva
                          </Button>
                        </CardFooter>
                      </Card>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold">Ideias para Pinterest</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="overflow-hidden">
                      <div className="aspect-[2/3] relative">
                        <img 
                          src="https://storage.googleapis.com/fenado-ai-farm-public/generated/c65fa8e5-3401-42f7-a6b3-cddae9dfc5f7.webp"
                          alt="Pinterest Pin Idea 1"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <CardContent className="p-3">
                        <h4 className="font-medium text-sm">Pin para Tutorial</h4>
                        <p className="text-xs text-muted-foreground mt-1">Design para tutoriais passo a passo</p>
                      </CardContent>
                    </Card>
                    <Card className="overflow-hidden">
                      <div className="aspect-[2/3] relative">
                        <img 
                          src="https://storage.googleapis.com/fenado-ai-farm-public/generated/7a3e9f96-8c70-4bb7-9d74-a5d2b8be8fe7.webp"
                          alt="Pinterest Pin Idea 2"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <CardContent className="p-3">
                        <h4 className="font-medium text-sm">Pin para Blog</h4>
                        <p className="text-xs text-muted-foreground mt-1">Design para promover posts de blog</p>
                      </CardContent>
                    </Card>
                    <Card className="overflow-hidden">
                      <div className="aspect-[2/3] relative">
                        <img 
                          src="https://storage.googleapis.com/fenado-ai-farm-public/generated/8c7a0217-9fba-4b1c-a0d9-e4db4a7de83f.webp"
                          alt="Pinterest Pin Idea 3"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <CardContent className="p-3">
                        <h4 className="font-medium text-sm">Pin para Produto</h4>
                        <p className="text-xs text-muted-foreground mt-1">Design para promover produtos</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="text-center mt-4">
                    <Button 
                      variant="outline"
                      onClick={() => window.open("https://br.pinterest.com/crisgride/", "_blank")}
                    >
                      Ver mais ideias no Pinterest <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex flex-col sm:flex-row gap-4">
                <Button 
                  className="w-full sm:w-auto"
                  onClick={handleDownloadCanva}
                >
                  <Download className="mr-2 h-4 w-4" /> Baixar Canva para Windows
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full sm:w-auto"
                  onClick={() => window.open("https://www.canva.com", "_blank")}
                >
                  <ExternalLink className="mr-2 h-4 w-4" /> Abrir Canva no navegador
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>

          <TabsContent value="adobe">
            <Card>
              <CardHeader>
                <CardTitle>Adobe Express - Criação de Vídeos e Animações</CardTitle>
                <CardDescription>
                  Templates e tutoriais para criar vídeos e animações profissionais para o YouTube e outras plataformas
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold">Templates populares</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                    {adobeTemplates.map((template) => (
                      <Card 
                        key={template.id} 
                        className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => setShowAdobeProject(true)}
                      >
                        <div className="aspect-video relative">
                          <img 
                            src={template.image} 
                            alt={template.name}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                            <Video className="h-8 w-8 text-white" />
                          </div>
                        </div>
                        <CardContent className="p-3">
                          <h4 className="font-medium text-sm">{template.name}</h4>
                          <p className="text-xs text-muted-foreground mt-1">{template.description}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold">Tutoriais</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {adobeTutorials.map((tutorial, index) => (
                      <Card key={index} className="overflow-hidden">
                        <CardHeader>
                          <CardTitle className="text-lg">{tutorial.title}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ol className="space-y-2">
                            {tutorial.steps.map((step, stepIndex) => (
                              <li key={stepIndex} className="flex items-start">
                                <span className="bg-primary/20 text-primary rounded-full h-5 w-5 flex items-center justify-center mr-2 text-xs flex-shrink-0 mt-0.5">
                                  {stepIndex + 1}
                                </span>
                                <span className="text-sm">{step}</span>
                              </li>
                            ))}
                          </ol>
                        </CardContent>
                        <CardFooter>
                          <Button 
                            variant="outline" 
                            className="w-full"
                            onClick={handleOpenAdobeExpress}
                          >
                            Experimentar no Adobe Express
                          </Button>
                        </CardFooter>
                      </Card>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold">Ideias para YouTube</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="overflow-hidden">
                      <div className="aspect-video relative">
                        <img 
                          src="https://storage.googleapis.com/fenado-ai-farm-public/generated/50ff2ff7-3f36-45f1-ad36-a2e4d5b7e424.webp"
                          alt="YouTube Idea 1"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                          <Video className="h-8 w-8 text-white" />
                        </div>
                      </div>
                      <CardContent className="p-3">
                        <h4 className="font-medium text-sm">Intro Animada</h4>
                        <p className="text-xs text-muted-foreground mt-1">Animação de introdução para vídeos</p>
                      </CardContent>
                    </Card>
                    <Card className="overflow-hidden">
                      <div className="aspect-video relative">
                        <img 
                          src="https://storage.googleapis.com/fenado-ai-farm-public/generated/c1c5cccf-2954-4022-8e81-3a7588f9a5c9.webp"
                          alt="YouTube Idea 2"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                          <Video className="h-8 w-8 text-white" />
                        </div>
                      </div>
                      <CardContent className="p-3">
                        <h4 className="font-medium text-sm">Lower Third</h4>
                        <p className="text-xs text-muted-foreground mt-1">Identificação na parte inferior do vídeo</p>
                      </CardContent>
                    </Card>
                    <Card className="overflow-hidden">
                      <div className="aspect-video relative">
                        <img 
                          src="https://storage.googleapis.com/fenado-ai-farm-public/generated/293ecf9e-6809-4f5c-a7f6-92e5366c6c85.webp"
                          alt="YouTube Idea 3"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                          <Video className="h-8 w-8 text-white" />
                        </div>
                      </div>
                      <CardContent className="p-3">
                        <h4 className="font-medium text-sm">End Screen</h4>
                        <p className="text-xs text-muted-foreground mt-1">Tela final com chamadas para ação</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="text-center mt-4">
                    <Button 
                      variant="outline"
                      onClick={() => window.open("https://www.adobe.com/express/create/animation/youtube", "_blank")}
                    >
                      Ver mais ideias para YouTube <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  className="w-full"
                  onClick={handleOpenAdobeExpress}
                >
                  <ExternalLink className="mr-2 h-4 w-4" /> Abrir Adobe Express
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>

        <Dialog open={showCanvaProject} onOpenChange={setShowCanvaProject}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Criar novo projeto no Canva</DialogTitle>
              <DialogDescription>
                Escolha uma imagem para começar seu design
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div 
                className={`aspect-video border-2 rounded-md overflow-hidden cursor-pointer ${selectedImage === "image1" ? "border-primary" : "border-border"}`}
                onClick={() => handleImageSelect("image1")}
              >
                <img 
                  src="https://storage.googleapis.com/fenado-ai-farm-public/generated/6e6751c8-8d0a-4d08-bc06-4c6a7a77b1e9.webp"
                  alt="Template 1"
                  className="w-full h-full object-cover"
                />
                {selectedImage === "image1" && (
                  <div className="absolute top-2 right-2 bg-primary rounded-full p-1">
                    <Check className="h-4 w-4 text-primary-foreground" />
                  </div>
                )}
              </div>
              <div 
                className={`aspect-video border-2 rounded-md overflow-hidden cursor-pointer ${selectedImage === "image2" ? "border-primary" : "border-border"}`}
                onClick={() => handleImageSelect("image2")}
              >
                <img 
                  src="https://storage.googleapis.com/fenado-ai-farm-public/generated/c65fa8e5-3401-42f7-a6b3-cddae9dfc5f7.webp"
                  alt="Template 2"
                  className="w-full h-full object-cover"
                />
                {selectedImage === "image2" && (
                  <div className="absolute top-2 right-2 bg-primary rounded-full p-1">
                    <Check className="h-4 w-4 text-primary-foreground" />
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="project-name">Nome do projeto</Label>
              <Input 
                id="project-name" 
                placeholder="Meu novo design" 
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
              />
            </div>
            <DialogFooter className="sm:justify-between">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setSelectedImage(null);
                  setProjectName("");
                  setShowCanvaProject(false);
                }}
              >
                Cancelar
              </Button>
              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => {
                    toast.success("Imagem selecionada para upload");
                  }}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Enviar imagem
                </Button>
                <Button 
                  type="submit"
                  onClick={handleCreateProject}
                  disabled={!selectedImage || !projectName}
                >
                  <FileImage className="mr-2 h-4 w-4" />
                  Criar projeto
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showAdobeProject} onOpenChange={setShowAdobeProject}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Criar novo projeto no Adobe Express</DialogTitle>
              <DialogDescription>
                Escolha um template de vídeo para começar
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div 
                className={`aspect-video border-2 rounded-md overflow-hidden cursor-pointer ${selectedImage === "adobe1" ? "border-primary" : "border-border"}`}
                onClick={() => handleImageSelect("adobe1")}
              >
                <img 
                  src="https://storage.googleapis.com/fenado-ai-farm-public/generated/e6e80f1f-a7ba-4eba-9d9a-4a6f9fd4c1a4.webp"
                  alt="Template 1"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  <Video className="h-8 w-8 text-white" />
                </div>
                {selectedImage === "adobe1" && (
                  <div className="absolute top-2 right-2 bg-primary rounded-full p-1">
                    <Check className="h-4 w-4 text-primary-foreground" />
                  </div>
                )}
              </div>
              <div 
                className={`aspect-video border-2 rounded-md overflow-hidden cursor-pointer ${selectedImage === "adobe2" ? "border-primary" : "border-border"}`}
                onClick={() => handleImageSelect("adobe2")}
              >
                <img 
                  src="https://storage.googleapis.com/fenado-ai-farm-public/generated/ab733b94-c614-4b83-b59a-bcf0134b37a6.webp"
                  alt="Template 2"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  <Video className="h-8 w-8 text-white" />
                </div>
                {selectedImage === "adobe2" && (
                  <div className="absolute top-2 right-2 bg-primary rounded-full p-1">
                    <Check className="h-4 w-4 text-primary-foreground" />
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="project-name">Nome do projeto</Label>
              <Input 
                id="project-name" 
                placeholder="Meu novo vídeo" 
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
              />
            </div>
            <DialogFooter className="sm:justify-between">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setSelectedImage(null);
                  setProjectName("");
                  setShowAdobeProject(false);
                }}
              >
                Cancelar
              </Button>
              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => {
                    toast.success("Vídeo selecionado para upload");
                  }}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Enviar vídeo
                </Button>
                <Button 
                  type="submit"
                  onClick={handleCreateProject}
                  disabled={!selectedImage || !projectName}
                >
                  <Video className="mr-2 h-4 w-4" />
                  Criar projeto
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
      <Footer />
    </div>
  );
};

export default DesignToolsPage;